import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as PendingIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { AnalysisJob, JobStatus } from '@signals/app-shell/types';

interface ProgressTrackerProps {
  job: AnalysisJob;
  onJobComplete?: (job: AnalysisJob) => void;
}

interface ProgressStep {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'completed' | 'error';
  message?: string;
  startTime?: Date;
  endTime?: Date;
}

const SimpleProgressTracker: React.FC<ProgressTrackerProps> = ({ job, onJobComplete }) => {
  const [currentJob, setCurrentJob] = useState<AnalysisJob>(job);
  const [progressSteps, setProgressSteps] = useState<ProgressStep[]>([]);

  // Update local job state when prop changes
  useEffect(() => {
    setCurrentJob(job);
  }, [job]);

  // Simple polling without complex dependencies
  useEffect(() => {
    // Only poll if job is active
    if (currentJob.status !== JobStatus.RUNNING && currentJob.status !== JobStatus.PENDING) {
      return;
    }

    const pollJobStatus = async () => {
      try {
        const response = await fetch(`/api/analysis/jobs/${currentJob.id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setCurrentJob(data);

          // Check if job is complete
          if (data.status === JobStatus.COMPLETED || data.status === JobStatus.FAILED || data.status === JobStatus.CANCELLED) {
            if (onJobComplete) {
              onJobComplete(data);
            }
          }
        }
      } catch (error) {
        console.error('Error polling job:', error);
      }
    };

    // Poll immediately
    pollJobStatus();

    // Then poll every 2 seconds
    const interval = setInterval(pollJobStatus, 2000);

    // Cleanup
    return () => clearInterval(interval);
  }, [currentJob.id, currentJob.status, onJobComplete]);

  // Generate progress steps based on job status
  useEffect(() => {
    const steps: ProgressStep[] = [
      {
        id: 'initialize',
        label: 'Initializing Analysis',
        status: currentJob.progress_percentage >= 5 ? 'completed' : currentJob.status === JobStatus.RUNNING ? 'running' : 'pending'
      },
      {
        id: 'extract',
        label: 'Extracting Data',
        status: currentJob.progress_percentage >= 20 ? 'completed' : currentJob.progress_percentage >= 10 ? 'running' : 'pending'
      },
      {
        id: 'dwells',
        label: 'Detecting Dwell Locations',
        status: currentJob.progress_percentage >= 40 ? 'completed' : currentJob.progress_percentage >= 25 ? 'running' : 'pending'
      },
      {
        id: 'routes',
        label: 'Analyzing Routes',
        status: currentJob.progress_percentage >= 60 ? 'completed' : currentJob.progress_percentage >= 45 ? 'running' : 'pending'
      },
      {
        id: 'patterns',
        label: 'Finding Patterns',
        status: currentJob.progress_percentage >= 80 ? 'completed' : currentJob.progress_percentage >= 65 ? 'running' : 'pending'
      },
      {
        id: 'insights',
        label: 'Generating AI Insights',
        status: currentJob.progress_percentage >= 99 ? 'completed' : currentJob.progress_percentage >= 95 ? 'running' : 'pending'
      },
      {
        id: 'complete',
        label: 'Finalizing Report',
        status: currentJob.status === JobStatus.COMPLETED ? 'completed' : currentJob.progress_percentage >= 99 ? 'running' : 'pending'
      }
    ];

    // Add error status if job failed
    if (currentJob.status === JobStatus.FAILED) {
      steps.forEach(step => {
        if (step.status !== 'completed') {
          step.status = 'error';
        }
      });
    }

    setProgressSteps(steps);
  }, [currentJob.progress_percentage, currentJob.status]);

  const getStatusIcon = (status: ProgressStep['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'running':
        return <CircularProgress size={20} />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <PendingIcon color="disabled" />;
    }
  };

  const getStatusColor = () => {
    switch (currentJob.status) {
      case JobStatus.COMPLETED:
        return 'success';
      case JobStatus.FAILED:
        return 'error';
      case JobStatus.CANCELLED:
        return 'warning';
      default:
        return 'primary';
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack spacing={3}>
          {/* Header */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Analysis Progress
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Chip
                label={currentJob.status}
                color={getStatusColor()}
                size="small"
              />
              {currentJob.progress_percentage !== undefined && (
                <Typography variant="body2" color="text.secondary">
                  {currentJob.progress_percentage}% Complete
                </Typography>
              )}
            </Stack>
          </Box>

          {/* Progress Bar */}
          <Box>
            <LinearProgress
              variant="determinate"
              value={currentJob.progress_percentage || 0}
              color={getStatusColor()}
              sx={{ height: 8, borderRadius: 1 }}
            />
            {currentJob.current_step && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {currentJob.current_step}
              </Typography>
            )}
          </Box>

          {/* Progress Steps */}
          <List dense>
            {progressSteps.map((step) => (
              <ListItem key={step.id}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {getStatusIcon(step.status)}
                </ListItemIcon>
                <ListItemText
                  primary={step.label}
                  primaryTypographyProps={{
                    color: step.status === 'running' ? 'primary' : 'text.primary',
                    fontWeight: step.status === 'running' ? 'medium' : 'normal'
                  }}
                />
              </ListItem>
            ))}
          </List>

          {/* Error Message */}
          {currentJob.error_message && (
            <Alert severity="error">
              {currentJob.error_message}
            </Alert>
          )}

          {/* Success Message */}
          {currentJob.status === JobStatus.COMPLETED && (
            <Alert severity="success">
              Analysis completed successfully!
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default SimpleProgressTracker;
